<script setup lang="ts">
defineProps<{
  ageGroups: string[];
  ageRows: any[];
  ageLoading: boolean;
  heatLegendAlphas: number[];
  rowMaxKey: (row: any) => string;
  cellBg: (value: number) => string;
  barWidth: (value: number) => string;
}>();
</script>

<template>
  <div class="hd-panel hd-age-panel">
    <div class="hd-panel-accent"></div>
    <div class="hd-panel-header">
      <h3 class="hd-panel-title">年齢・性別層分析</h3>
      <div class="hd-heat-legend">
        <span class="hd-heat-legend-label">LOW</span>
        <div class="hd-heat-legend-bars">
          <span v-for="(a, i) in heatLegendAlphas" :key="i" class="hd-heat-legend-bar" :style="{ background: `rgba(239, 68, 68, ${a})` }"></span>
        </div>
        <span class="hd-heat-legend-label">HIGH</span>
      </div>
    </div>
    <div class="hd-table-scroll">
      <table class="hd-age-table">
        <thead>
          <tr>
            <th rowspan="2" class="hd-col-keyword">キーワード</th>
            <th v-for="(c, i) in ageGroups" :key="i" colspan="2" class="hd-col-age-group">{{ c }}</th>
            <th colspan="2" class="hd-col-age-group hd-col-all-age-group">全世代</th>
          </tr>
          <tr>
            <template v-for="(c, i) in ageGroups" :key="i">
              <th class="hd-col-age-sub">男</th>
              <th class="hd-col-age-sub">女</th>
            </template>
            <th class="hd-col-age-sub">男</th>
            <th class="hd-col-age-sub">女</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="ageLoading"><td :colspan="1 + ageGroups.length * 2 + 2" class="hd-cell-empty">読み込み中...</td></tr>
          <tr v-else-if="ageRows.length === 0"><td :colspan="1 + ageGroups.length * 2 + 2" class="hd-cell-empty">データがありません</td></tr>
          <tr v-for="row in ageRows" :key="row.keyword">
            <td class="hd-cell-keyword"><span class="hd-keyword-dot" :style="{ background: row.dotColor }"></span><span class="hd-keyword-text">{{ row.keyword }}</span></td>
            <template v-for="(cell, ci) in row.cells" :key="ci">
              <td class="hd-cell-value" :class="{ 'is-max': rowMaxKey(row) === `${ci}-male` }" :style="{ background: cellBg(cell.male) }"><div class="hd-cell-value-inner"><div class="hd-mini-bar"><span class="hd-mini-bar-fill hd-mini-bar-fill--male" :style="{ width: barWidth(cell.male) }"></span></div><span class="hd-cell-value-text hd-text-male">{{ cell.male }}%</span></div></td>
              <td class="hd-cell-value" :class="{ 'is-max': rowMaxKey(row) === `${ci}-female` }" :style="{ background: cellBg(cell.female) }"><div class="hd-cell-value-inner"><div class="hd-mini-bar"><span class="hd-mini-bar-fill hd-mini-bar-fill--female" :style="{ width: barWidth(cell.female) }"></span></div><span class="hd-cell-value-text hd-text-female">{{ cell.female }}%</span></div></td>
            </template>
            <td class="hd-cell-value" :class="{ 'is-max': rowMaxKey(row) === 'all-male' }" :style="{ background: cellBg(row.maleTotalPercent) }"><div class="hd-cell-value-inner"><div class="hd-mini-bar"><span class="hd-mini-bar-fill hd-mini-bar-fill--male" :style="{ width: barWidth(row.maleTotalPercent) }"></span></div><span class="hd-cell-value-text hd-text-male">{{ row.maleTotalPercent }}%</span></div></td>
            <td class="hd-cell-value" :class="{ 'is-max': rowMaxKey(row) === 'all-female' }" :style="{ background: cellBg(row.femaleTotalPercent) }"><div class="hd-cell-value-inner"><div class="hd-mini-bar"><span class="hd-mini-bar-fill hd-mini-bar-fill--female" :style="{ width: barWidth(row.femaleTotalPercent) }"></span></div><span class="hd-cell-value-text hd-text-female">{{ row.femaleTotalPercent }}%</span></div></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped lang="less">
.hd-panel { border: 1px solid var(--border-color); border-radius: 6px; background: var(--bg-panel); overflow: hidden; color: var(--text-primary); }
.hd-panel-accent { height: 3px; background: linear-gradient(90deg, var(--accent-orange) 0%, #eab308 100%); }
.hd-panel-header { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid var(--border-color); }
.hd-panel-title { margin: 0; font-size: 12px; font-weight: 700; color: var(--text-primary); }
.hd-heat-legend { display: flex; align-items: center; gap: 4px; }
.hd-heat-legend-label { font-size: 9px; color: var(--text-tertiary); }
.hd-heat-legend-bars { display: flex; gap: 1px; }
.hd-heat-legend-bar { width: 12px; height: 10px; border-radius: 1px; }
.hd-table-scroll { width: 100%; overflow-x: auto; }
.hd-age-table { width: 100%; min-width: 1398px; border-collapse: collapse; table-layout: fixed; }
.hd-age-table th, .hd-age-table td { border-bottom: 1px solid var(--border-color); }
.hd-age-table thead th { background: var(--bg-body); font-size: 10px; color: var(--text-secondary); text-align: center; padding: 0 12px; height: 35px; }
.hd-col-keyword { text-align: left !important; width: 166px; }
.hd-cell-keyword { padding: 0 12px; border-right: 1px solid var(--border-color); }
.hd-cell-empty { height: 48px; text-align: center; font-size: 14px; color: var(--text-tertiary); }
.hd-keyword-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; margin-right: 6px; }
.hd-keyword-text { font-size: 11px; font-weight: 600; color: var(--text-primary); }
.hd-cell-value { height: 33px; padding: 0 6px; text-align: right; }
.hd-cell-value-inner { display: inline-flex; align-items: center; gap: 6px; width: 100%; justify-content: flex-end; }
.hd-mini-bar { width: 44px; height: 3px; background: var(--border-color); border-radius: 9999px; overflow: hidden; }
.hd-mini-bar-fill { display: block; height: 3px; border-radius: 9999px; background: var(--accent-red); }
.hd-mini-bar-fill--male { background: var(--accent-blue); }
.hd-mini-bar-fill--female { background: var(--accent-pink); }
.hd-cell-value-text { width: 28px; font-size: 10px; text-align: right; }
.hd-text-male { color: var(--accent-blue); }
.hd-text-female { color: var(--accent-pink); }
.is-max .hd-cell-value-text { font-weight: 900; }
</style>
